import React, { useEffect, useState } from 'react';
import {
  Box, Text, VStack, useToken,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import Svg, { Circle } from 'react-native-svg';

import { useOralExamQuestionTimer } from '@/common/hooks/oralExam/useOralExamQuestionTimer';
import { serverNow } from '@/common/time/serverNow';

import {
  formatOralExamTimer,
  getOralExamPreReadSecondsRemaining,
  getOralExamQuestionPhase,
  type OralExamQuestionPhase,
} from './oralExamQuestionPhase';

type AuthoritativeCountdownProps = {
  secondsPerQuestion: number;
  questionMarkerStartedAt?: string | null;
  questionDeadline?: string | null;
  isPaused?: boolean;
  /**
   * The AI examiner runs this exam. Its deadline is projected before speech
   * ends so a silent examiner cannot freeze the clock; that gap is not a
   * look-window, so the ring stays full and the hint says the answer timer
   * starts when reading finishes. At 0:00 the server shows the next question,
   * so there is no "time's up" wait to announce.
   */
  aiExaminer?: boolean;
  /** Ring diameter in px; default 88. Compact rail uses `COMPACT_COUNTDOWN_RING_SIZE`. */
  ringSize?: number;
};

/** Shared 1:1 / lecturer-engage diameter: 30% less than the previous 168px ring. */
export const ONE_TO_ONE_COUNTDOWN_RING_SIZE = 118;
export const COMPACT_COUNTDOWN_RING_SIZE = 64;
export const COMPACT_COUNTDOWN_RING_SIZE_NARROW = 56;
export const DEFAULT_COUNTDOWN_RING_SIZE = 88;
/** Shared stroke — 2× the previous 4px ring so the arc reads as thicker. */
export const COUNTDOWN_STROKE_WIDTH = 8;
/** Rail-sized rings read heavy at 8px; a thinner arc keeps the clock face open. */
export const COMPACT_COUNTDOWN_STROKE_WIDTH = 4;

function strokeWidthForRing(ringSize: number): number {
  return ringSize <= COMPACT_COUNTDOWN_RING_SIZE
    ? COMPACT_COUNTDOWN_STROKE_WIDTH
    : COUNTDOWN_STROKE_WIDTH;
}

function timeTextSizeForRing(ringSize: number): 'md' | 'lg' | '2xl' | '3xl' {
  if (ringSize <= COMPACT_COUNTDOWN_RING_SIZE) return 'md';
  if (ringSize >= 160) return '3xl';
  if (ringSize >= ONE_TO_ONE_COUNTDOWN_RING_SIZE) return '2xl';
  return 'lg';
}

function ringColorToken(
  phase: OralExamQuestionPhase,
  remainingSeconds: number,
  totalSeconds: number,
): '$brandBase' | '$statusWarning' | '$borderLight' {
  if (phase === 'reading') return '$borderLight';
  if (totalSeconds <= 0) return '$brandBase';
  const fraction = remainingSeconds / totalSeconds;
  return fraction <= 0.25 ? '$statusWarning' : '$brandBase';
}

/**
 * Server-deadline countdown: full ring while the examiner is still reading,
 * depleting during answering. A lecturer look-window counts down; an agent
 * exam keeps the reading hint until speech ends.
 */
export function AuthoritativeCountdown({
  secondsPerQuestion,
  questionMarkerStartedAt,
  questionDeadline,
  isPaused = false,
  aiExaminer = false,
  ringSize: ringSizeProp,
}: AuthoritativeCountdownProps) {
  const { t } = useTranslation();
  const ringSize = ringSizeProp ?? DEFAULT_COUNTDOWN_RING_SIZE;
  const strokeWidth = strokeWidthForRing(ringSize);
  const ringRadius = (ringSize - strokeWidth) / 2;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const timeTextSize = timeTextSizeForRing(ringSize);
  const hintTextSize = ringSize <= 64 ? 'xs' : 'sm';
  // @ts-expect-error - brandBase is a semantic token
  const brandBase = useToken('colors', 'brandBase') as string;
  // @ts-expect-error - statusWarning is a semantic token
  const statusWarning = useToken('colors', 'statusWarning') as string;
  // @ts-expect-error - borderLight is a semantic token
  const borderLight = useToken('colors', 'borderLight') as string;
  const tokenColors = {
    $brandBase: brandBase,
    $statusWarning: statusWarning,
    $borderLight: borderLight,
  };

  const [tick, setTick] = useState(0);
  // tick advances each second during pre_read so phase/N stay live
  const phaseNow = serverNow() + tick * 0;
  const phase = getOralExamQuestionPhase(
    questionMarkerStartedAt,
    questionDeadline,
    secondsPerQuestion,
    phaseNow,
  );
  const isAnswering = phase === 'answering';
  const isPreRead = phase === 'pre_read';
  const showReadingHint = phase === 'reading'
    || (isPreRead && aiExaminer);

  useEffect(() => {
    if (!questionDeadline || isPaused || phase === 'reading') return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [questionDeadline, isPaused, phase]);

  const preReadSeconds = isPreRead && questionDeadline
    ? getOralExamPreReadSecondsRemaining(questionDeadline, secondsPerQuestion, phaseNow)
    : 0;

  const { remainingSeconds, formatted } = useOralExamQuestionTimer(
    secondsPerQuestion,
    isAnswering,
    questionDeadline ?? null,
    isPaused,
  );

  const displayTime = isAnswering
    ? formatted
    : formatOralExamTimer(secondsPerQuestion);

  const progress = isAnswering && secondsPerQuestion > 0
    ? remainingSeconds / secondsPerQuestion
    : 1;

  const isTimeUp = isAnswering && remainingSeconds === 0 && !aiExaminer;

  const hint = isTimeUp
    ? t('oralExam.countdownTimeUp', { defaultValue: "Time's up — waiting for the examiner" })
    : showReadingHint
      ? t('oralExam.timerDuringReading', {
        defaultValue: 'Answer timer starts after the AI examiner finishes reading',
      })
      : isPreRead
        ? t('oralExam.preReadCountdown', {
          seconds: preReadSeconds,
          defaultValue: 'Look at the question. Answering time starts in {{seconds}}',
        })
        : t('oralExam.countdownAnsweringHint', { defaultValue: 'Answer time remaining' });

  const ringOpacity = phase === 'reading' ? 0.55 : 0.65 + progress * 0.35;
  const progressToken = ringColorToken(phase, remainingSeconds, secondsPerQuestion);
  const progressColor = tokenColors[progressToken];
  const trackColor = tokenColors.$borderLight;
  const strokeDashoffset = ringCircumference * (1 - progress);

  return (
    <VStack space="lg" alignItems="center" testID="oral-exam-countdown">
      <Box
        testID="oral-exam-countdown-ring-host"
        w={ringSize}
        h={ringSize}
        alignItems="center"
        justifyContent="center"
        opacity={ringOpacity}
        accessibilityValue={{
          min: 0,
          max: secondsPerQuestion,
          now: isAnswering ? remainingSeconds : secondsPerQuestion,
        }}
      >
        <Svg
          width={ringSize}
          height={ringSize}
          style={{ position: 'absolute' }}
        >
          <Circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={ringRadius}
            stroke={trackColor}
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <Circle
            testID="oral-exam-countdown-ring"
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={ringRadius}
            stroke={progressColor}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={`${ringCircumference} ${ringCircumference}`}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            rotation={-90}
            origin={`${ringSize / 2}, ${ringSize / 2}`}
          />
        </Svg>
        <Box
          w={ringSize - strokeWidth * 2}
          h={ringSize - strokeWidth * 2}
          borderRadius="$full"
          alignItems="center"
          justifyContent="center"
          bg="$bgCard"
        >
          <Text
            testID="oral-exam-countdown-time"
            size={timeTextSize}
            fontWeight="semibold"
            color="$textHeading"
          >
            {displayTime}
          </Text>
        </Box>
      </Box>
      <Text
        size={hintTextSize}
        color="$textSubtle"
        textAlign="center"
        testID={
          isTimeUp
            ? 'oral-exam-countdown-time-up'
            : showReadingHint
              ? 'oral-exam-countdown-reading'
              : phase === 'pre_read'
                ? 'oral-exam-countdown-pre-read'
                : `oral-exam-countdown-${phase}`
        }
      >
        {hint}
      </Text>
    </VStack>
  );
}
